import { startEval } from './startEval'
import {
  findSlides,
  getRes,
  disableSwipe,
  restartSlides,
  tooltipListener,
  aboutRolloverTooltip,
  clearResultsPage,
  restartResultsSlider,
  lockBodyScroll,
  unlockBodyScroll
} from './utils'
import downloadResults from './downloadResults'
import { CardCarousel } from '../CardCarousel/index'
import './rollover.css'
import xss from 'xss'

type RolloverCarousel = ReturnType<typeof CardCarousel>

// 'results' and 'landing' are terminal view markers; a number is the 1-based
// question index. Mirrors the shape pushed into history.state.
type RolloverView = number | 'results' | 'landing'

// Per-document rollover runtime state. Fields are populated incrementally after
// `document.rollover` is initialized to `{}`, so each is optional. `state` is
// null before the first view is set.
interface RolloverState {
  state?: RolloverView | null
  count?: number
}

declare global {
  interface Document {
    rollover: RolloverState
  }
}

interface RolloverHistoryState {
  view: RolloverView
}

// The share form is a custom element that exposes an `answers` property used to
// seed the form with the rendered results HTML. Not part of the DOM lib types.
interface ShareFormElement extends HTMLElement {
  answers: string
}

// Arrow navigation methods on the carousel, selected dynamically by direction.
type ArrowDirection = 'leftArrowEvent' | 'rightArrowEvent'

/*
 * Rollover - Download method
 */
function setupDownloadLink(): void {
  const rolloverDownloadBts = document.querySelector(
    '.rollover-evaluator-header-results-buttons'
  )
  const summaryBtns = rolloverDownloadBts?.querySelectorAll('.Button')
  if (summaryBtns && summaryBtns.length > 0) {
    const downloadResultBtn = summaryBtns[1]
    downloadResultBtn?.addEventListener('click', event => {
      event.preventDefault()
      void callDownloadHtml()
    })
  }
}

async function callDownloadHtml(): Promise<void> {
  /* Read the INNERHTML from the results and pass it to the DOWNLOAD Method */
  const resultsDOM = document.querySelector(
    '#rollover-results-download-content-pdf'
  )
  if (resultsDOM) {
    try {
      await downloadResults(resultsDOM.innerHTML)
    } catch (error) {
      console.error(error)
    }
  }
}

const rolloverComponent = document.querySelector('.rollover-component')
if (rolloverComponent) {
  document.rollover = {}
  directToLandingPageView()
  setupSwiper()
  aboutRolloverTooltip()
  preventTooltipLinkJumps(rolloverComponent)
  setupDownloadLink()
}

// user should be redirected to the landing page if trying to go directly to a slide
function directToLandingPageView(): void {
  document.rollover.state = null
  history.replaceState({ view: 'landing' }, '', xss(window.location.pathname))
}

function setupSwiper(): void {
  const results = document.querySelector('.swiper-container.results')
  const evaluator = document.querySelector<HTMLElement>('.rollover-evaluator')
  const resultsView = document.querySelector<HTMLElement>(
    '.rollover-results-view'
  )
  const resultsSummary = document.querySelector<HTMLElement>(
    '.rollover-results-summary'
  )
  if (!evaluator || !resultsView) return

  const start = evaluator.querySelector<HTMLElement>('.start-eval')
  const container = evaluator.querySelector<HTMLElement>('.swiper-container')
  const overview = resultsView.querySelector('.pros-overview')
  const resultsHeaderView = document.querySelector<HTMLElement>(
    '.rollover-evaluator-header-results'
  )
  const wrapper = container?.querySelector('.swiper-wrapper')
  if (wrapper) {
    document.rollover.count = wrapper.children.length
  }

  if (!container) return

  let resultsSwiper: RolloverCarousel | undefined
  if (results) {
    resultsSwiper = CardCarousel(results as ParentNode as Document)
  }

  const userEntrySwiper = CardCarousel(container as ParentNode as Document, {
    tabbing: true
  })
  const nextButton = container.querySelector("[data-action='slideRight']")
  userEntrySwiper.init()
  startEval(direction => {
    findSlides()
    updateURLwithQueryQuestions(userEntrySwiper.currentCardIndex(), direction)
  })

  window.addEventListener('popstate', evt => {
    const state = evt.state as RolloverHistoryState | null
    if (!state) return
    if (state.view === 'landing') {
      if (start) start.style.display = 'block'
      container.style.display = 'none'
    } else if (state.view === 'results') {
      if (overview?.textContent) {
        const activeSlide = evaluator.querySelector<HTMLElement>(
          '.swiper-slide.active'
        )
        clearResultsPage()
        if (activeSlide) getRes(activeSlide)
        evaluator.style.display = 'none'
        resultsView.style.display = 'block'
        if (resultsHeaderView) resultsHeaderView.style.display = 'block'
        if (resultsSummary) resultsSummary.style.display = 'block'
      } else {
        history.forward()
      }
    } else if (document.rollover.state === 'landing') {
      if (start) start.style.display = 'none'
      container.style.display = 'block'
      if (resultsHeaderView) resultsHeaderView.style.display = 'none'
    } else if (document.rollover.state === 'results') {
      evaluator.style.display = 'block'
      resultsView.style.display = 'none'
      if (resultsHeaderView) resultsHeaderView.style.display = 'none'
      if (resultsSummary) resultsSummary.style.display = 'none'
    } else {
      const direction: ArrowDirection =
        Number(document.rollover.state) > Number(state.view)
          ? 'leftArrowEvent'
          : 'rightArrowEvent'
      if (direction === 'rightArrowEvent') {
        const activeSlide = evaluator.querySelector<HTMLElement>(
          '.swiper-slide.active'
        )
        if (activeSlide) getRes(activeSlide)
      }
      userEntrySwiper[direction]()
    }

    document.rollover.state = state.view
  })

  userEntrySwiper.on(
    'slideNextTransitionStart',
    function (this: HTMLElement): void {
      const activeSlide = evaluator.querySelector<HTMLElement>(
        '.swiper-slide.active'
      )
      if (!activeSlide) return
      const isLastSlide = activeSlide.classList.contains('is-last')
      const isRightClick = this.dataset?.action === 'slideRight'
      const isInitState = overview?.textContent === ''

      if (isRightClick && isLastSlide && isInitState) {
        getRes(activeSlide)
        if (results) {
          resultsSwiper = CardCarousel(results as ParentNode as Document, {
            defaultNav: true
          })
          resultsSwiper.init()
        }
      } else if (isRightClick && isLastSlide) {
        clearResultsPage()
        getRes(activeSlide)
      } else if (isRightClick) {
        getRes(activeSlide)
      }
    }
  )

  nextButton?.addEventListener('click', () => {
    updateURLwithQueryQuestions(userEntrySwiper.currentCardIndex(), 'next')
  })
  userEntrySwiper.on('transitionEnd', () => {
    disableSwipe()
    findSlides()
    tooltipListener()
  })

  setupShare()
  setupRestart(userEntrySwiper, resultsSwiper)
}

function setupRestart(
  userEntrySwiper: RolloverCarousel,
  resultsSwiper: RolloverCarousel | undefined
): void {
  // restart
  const restartButton = document.querySelector('.restartLink')
  if (restartButton) {
    restartButton.addEventListener('click', () => {
      clearResultsPage()
      userEntrySwiper.slideTo(0)
      updateURLwithQueryQuestions()
      restartSlides()
      if (resultsSwiper) {
        restartResultsSlider(resultsSwiper)
      }
    })
  }
}

function setupShare(): void {
  const shareAdvisorButton = document.querySelector('.shareAdvisor')
  const shareModalForm = document.querySelector<HTMLElement>(
    '.Modal.rollover-share'
  )
  const shareClose = shareModalForm?.querySelector('.close-modal')

  if (shareAdvisorButton && shareModalForm) {
    shareAdvisorButton.addEventListener('click', () => {
      shareModalForm.style.display = 'block'
      lockBodyScroll()
      const shareForm = document.querySelector<ShareFormElement>('.contact-ira')
      const resultsDOM = document.querySelector(
        '#rollover-results-download-content-pdf'
      )
      if (resultsDOM) {
        const resultsHTML = resultsDOM.innerHTML || ''
        if (shareForm) shareForm.answers = resultsHTML
      }
    })
  }
  if (shareClose && shareModalForm) {
    shareClose.addEventListener('click', () => {
      shareModalForm.style.display = 'none'
      unlockBodyScroll()
    })
  }
}

function updateURLwithQueryQuestions(currentSlide = 0, direction = ''): void {
  const view = currentSlide + 1
  const { rollover } = document
  const sanitizedPathName = xss(window.location.pathname)
  if (rollover.state === rollover.count && direction === 'next') {
    rollover.state = 'results'
    history.pushState({ view: 'results' }, '', sanitizedPathName + '?results')
  } else if (rollover.state === 1 && direction === 'prev') {
    rollover.state = 'landing'
    history.pushState({ view: 'landing' }, '', sanitizedPathName)
  } else {
    rollover.state = view
    history.pushState({ view }, '', sanitizedPathName + '?question=' + view)
  }
}

function preventTooltipLinkJumps(component: Element): void {
  component.querySelectorAll('.rollover-question a').forEach(element => {
    element.addEventListener('click', e => e.preventDefault())
  })
}

export { setupSwiper, updateURLwithQueryQuestions }
